import { PAGE_PATHS } from "./constants";
import { normalizePathname, pageFromPath } from "./navigation";
import { EN_ONLY_PATH_PREFIXES, guideSlugForLocale } from "./localeRouteRegistry";

const VI_PREFIX = "/vi";

/** Strip /vi prefix for SPA page resolution; keep /vi/ as root for Vietnamese home. */
export function stripViPrefix(pathname) {
  const path = normalizePathname(pathname);
  if (path === VI_PREFIX) return "/";
  if (path.startsWith(`${VI_PREFIX}/`)) return path.slice(VI_PREFIX.length) || "/";
  return path;
}

export function localeFromPath(pathname) {
  const path = normalizePathname(pathname);
  return path === VI_PREFIX || path.startsWith(`${VI_PREFIX}/`) ? "vi" : "en";
}

/** URL path (and optional server-injected locale) are the source of truth for client locale. */
export function resolveClientLocale(serverLocale) {
  const fromServer =
    serverLocale != null && String(serverLocale).trim() !== ""
      ? String(serverLocale).trim().toLowerCase().slice(0, 2)
      : null;
  const fromPath = typeof window !== "undefined" ? localeFromPath(window.location.pathname) : "en";
  const candidate = fromServer === "vi" || fromServer === "en" ? fromServer : fromPath;
  return candidate === "vi" ? "vi" : "en";
}

/** Active locale for non-React modules (HTTP, article widgets). Never reads localStorage. */
export function activeClientLocale() {
  if (typeof window === "undefined") return "en";
  return resolveClientLocale(window.ECLIPTICA_HTML_LOCALE);
}

/** Map SPA page id to localized URL path. */
export function localizedPath(page, locale) {
  const base = PAGE_PATHS[page] || PAGE_PATHS.landing;
  if (locale === "vi") {
    if (base === "/") return `${VI_PREFIX}/`;
    return `${VI_PREFIX}${base}`;
  }
  return base;
}

function applyViPrefix(path, targetLocale) {
  const normalized = normalizePathname(path);
  if (targetLocale === "vi") {
    if (normalized === "/") return `${VI_PREFIX}/`;
    if (normalized.startsWith(`${VI_PREFIX}/`) || normalized === VI_PREFIX) return normalized;
    return `${VI_PREFIX}${normalized}`;
  }
  return stripViPrefix(normalized);
}

function switchGuidePath(pathname, targetLocale) {
  const path = stripViPrefix(pathname);
  const match = path.match(/^\/guides\/([^/]+)\/?$/);
  if (!match) return null;
  const mapped = guideSlugForLocale(match[1], targetLocale);
  if (!mapped) return null;
  const base = `/guides/${mapped}`;
  return targetLocale === "vi" ? `${VI_PREFIX}${base}` : base;
}

function switchEnOnlyPath(pathname, targetLocale) {
  const path = stripViPrefix(pathname);
  if (!EN_ONLY_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return null;
  }
  // Articles are English-only; always use EN path regardless of target locale.
  return path;
}

function preserveQueryString(pathname, targetPath) {
  if (typeof window === "undefined") return targetPath;
  const current = normalizePathname(window.location.pathname);
  const input = normalizePathname(pathname);
  if (current !== input) return targetPath;
  const search = window.location.search;
  return search ? `${targetPath}${search}` : targetPath;
}

export function localizedPathFromPathname(pathname, targetLocale) {
  const enOnly = switchEnOnlyPath(pathname, targetLocale);
  if (enOnly != null) return preserveQueryString(pathname, enOnly);

  const guide = switchGuidePath(pathname, targetLocale);
  if (guide != null) return preserveQueryString(pathname, guide);

  const page = pageFromPath(stripViPrefix(pathname));
  return preserveQueryString(pathname, localizedPath(page, targetLocale));
}

export function switchLocaleUrl(pathname, targetLocale) {
  return localizedPathFromPathname(pathname, targetLocale);
}

/** Articles are English-only; always return /articles. */
export function localizedArticlesPath(_locale, page) {
  if (page != null && page > 1) return `/articles?page=${page}`;
  return "/articles";
}

/** Localize a raw English path (e.g. POST /login) using the active or given locale. */
export function localizedRawPath(englishPath, locale) {
  const path = englishPath?.toString() || "/";
  const resolvedLocale = locale || (typeof window !== "undefined" ? localeFromPath(window.location.pathname) : "en");
  if (resolvedLocale === "vi") return `${VI_PREFIX}${path}`;
  return path;
}

/** Localize a PAGE_PATHS page id and append query string. */
export function localizedPathWithQuery(page, locale, query = {}) {
  const base = localizedPath(page, locale);
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && value !== "") params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
